// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: decisions/0078-direct-installs-get-a-billing-account-at-enrollment.md

import * as React from 'react';
import { CheckCircle2, ExternalLink, Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { StepHeader } from '@/components/install/StepHeader';
import { api } from '@/lib/api';
import { thrownCode } from '@/lib/apiErrors';

/** How long the step waits for Stripe's webhook to reach Shift after the return. */
export const CONFIRM_POLLS = 5;
export const CONFIRM_POLL_MS = 3_000;

const STATUS_KEY = ['install-payment'];

interface PaymentStepProps {
  /** Stripe sent the installer back here (`?payment=returned`). */
  returned: boolean;
  /** Skip, Continue, or done: completes the step, which records nothing else. */
  onContinue: () => Promise<void>;
  isSubmitting: boolean;
  /** Where the Stripe page opens; injectable for tests. */
  navigate?: (url: string) => void;
}

/**
 * The install wizard's optional Payment method step for a direct install
 * (ADR-0078). A card is saved on a Stripe-hosted page; the step shows "saved"
 * only once Shift reports a payment method on file, never on the redirect.
 * Every way it can fail ends in one line and Continue, so it never blocks
 * finishing the install.
 */
export function PaymentStep({
  returned,
  onContinue,
  isSubmitting,
  navigate = (url) => window.location.assign(url),
}: PaymentStepProps): React.JSX.Element {
  const [polls, setPolls] = React.useState(0);
  const status = api.install.getPaymentStatus.useQuery(STATUS_KEY, {}, { queryKey: STATUS_KEY });
  const session = api.install.requestPaymentSession.useMutation();
  const [sessionProblem, setSessionProblem] = React.useState<string | null>(null);

  const body = status.data?.status === 200 ? status.data.body : null;
  const onFile = body?.status === 'available' && body.payment_method_on_file === true;
  const confirming = returned && !onFile && polls < CONFIRM_POLLS && !status.isError;

  const { refetch, isFetching } = status;
  React.useEffect(() => {
    if (!confirming || isFetching) return;
    const timer = window.setTimeout(() => {
      setPolls((n) => n + 1);
      void refetch();
    }, CONFIRM_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [confirming, isFetching, polls, refetch]);

  const addPaymentMethod = async (): Promise<void> => {
    setSessionProblem(null);
    try {
      const result = await session.mutateAsync({ body: {} });
      if (result.status !== 200) throw result;
      navigate(result.body.url);
    } catch (err) {
      const code = thrownCode(err);
      if (code === 'billing.payment_method_on_file') {
        await status.refetch();
        return;
      }
      setSessionProblem(sessionMessage(code));
    }
  };

  const busy = isSubmitting || session.isPending;
  const continueButton = (label: string) => (
    <Button
      type="button"
      className="w-full md:w-auto h-11 md:h-9"
      disabled={busy}
      onClick={() => void onContinue()}
      data-testid="step-next"
    >
      {label}
    </Button>
  );

  let content: React.ReactNode;
  if (status.isLoading) {
    content = (
      <p className="text-sm text-muted-foreground" aria-busy="true" data-testid="payment-loading">
        <Loader2 className="inline size-4 animate-spin mr-2" aria-hidden />
        Checking with Shift…
      </p>
    );
  } else if (onFile) {
    content = (
      <>
        <p className="text-sm text-foreground flex items-center gap-2" data-testid="payment-saved">
          <CheckCircle2 className="size-4 text-primary shrink-0" aria-hidden />A payment method is
          on file. You can change it in Settings → Billing after you sign in.
        </p>
        {continueButton('Continue')}
      </>
    );
  } else if (confirming) {
    content = (
      <p
        className="text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
        data-testid="payment-confirming"
      >
        <Loader2 className="inline size-4 animate-spin mr-2" aria-hidden />
        Confirming your payment method with Shift…
      </p>
    );
  } else if (returned) {
    content = (
      <>
        <p className="text-sm text-muted-foreground" data-testid="payment-pending">
          Shift will confirm your payment method shortly. You can check it in Settings → Billing
          after you sign in.
        </p>
        {continueButton('Continue')}
      </>
    );
  } else {
    const unavailable = unavailableMessage(body, status.isError);
    content =
      unavailable !== null ? (
        <>
          <p className="text-sm text-muted-foreground" data-testid="payment-unavailable">
            {unavailable}
          </p>
          {continueButton('Continue')}
        </>
      ) : (
        <>
          {sessionProblem !== null && (
            <Alert data-testid="payment-error">
              <AlertDescription>{sessionProblem}</AlertDescription>
            </Alert>
          )}
          <div className="flex flex-col gap-3 md:flex-row">
            <Button
              type="button"
              className="w-full md:w-auto h-11 md:h-9"
              disabled={busy}
              onClick={() => void addPaymentMethod()}
              data-testid="payment-add"
            >
              {session.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <ExternalLink className="size-4" aria-hidden />
              )}
              Add payment method
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full md:w-auto h-11 md:h-9"
              disabled={busy}
              onClick={() => void onContinue()}
              data-testid="payment-skip"
            >
              Skip for now
            </Button>
          </div>
        </>
      );
  }

  return (
    <div className="space-y-6" data-testid="step-payment">
      <StepHeader step="payment" title="Add a payment method">
        Save a card on a secure page hosted by Stripe, Shift&rsquo;s payment provider. Greenlight
        never sees card details, and saving a card charges nothing today. You can also add one later
        in Settings → Billing, within seven days of installing; after that, new building pauses
        until a card is saved.
      </StepHeader>
      {content}
    </div>
  );
}

function unavailableMessage(
  body: { status: string; hosted_management?: boolean } | null,
  failed: boolean,
): string | null {
  if (failed || body === null || body.status === 'unavailable') {
    return 'Shift can’t be reached right now. Add a payment method later in Settings → Billing.';
  }
  if (body.status === 'not_enrolled' || body.status === 'no_account') {
    return 'This install isn’t connected to Shift’s billing yet. Add a payment method later in Settings → Billing.';
  }
  if (body.hosted_management === false) {
    return 'Payment for this install is arranged with Shift directly, so there’s nothing to add here.';
  }
  return null;
}

function sessionMessage(code: string | undefined): string {
  if (code === 'billing.stripe_link_capacity') {
    return 'Saving a payment method is paused right now. Skip for now and add one later in Settings → Billing.';
  }
  if (code === 'install.already_complete') {
    return 'This install has already finished. Add a payment method in Settings → Billing.';
  }
  if (code === 'rate_limit.exceeded') {
    return 'Too many tries. Wait a few minutes, or skip for now.';
  }
  return 'Couldn’t open the payment page. Try again, or skip for now and add one later in Settings → Billing.';
}
